/*
 * Figma `121:672` — OPS-04 · Risks · Register + Consequences.
 *
 * `TableTemplate`: the register beside a 5×5 matrix, with the risk you opened in a drawer.
 *
 * The brief asks risk to show "impact × likelihood, owner, trigger, and mitigation", and the part
 * the old panel left implicit is the first. A number between 1 and 25 is not a decision; a *band*
 * is. `bandOf` is the single rule both the register and the matrix read, because a register saying
 * "high" beside a matrix putting the same risk in the amber square is worse than showing neither.
 *
 * Accepting a risk is the consequential action here, and it is not a status change. It is a signed
 * waiver with a reason and conditions, recorded against a person — so it goes through a dialog that
 * says the risk is being accepted rather than removed, and only an owner can do it, mirroring the
 * server.
 */
import { useState } from 'react';
import {
  Alert, Button, Card, Drawer, FormDialog, KpiCard, Select, StatusBadge, TableTemplate, TextField,
  Textarea,
} from '../..';
import type { BadgeTone, Column } from '../..';
import type { Destination } from '../../destinations';
import type { OpsRiskDto, OpsRiskStatus } from '../../../api';
import type { OpsRisksState, RiskBand } from './useOpsRisks';
import { bandOf, useOpsRisks } from './useOpsRisks';

const BAND_LABEL: Record<RiskBand, string> = {
  critical: 'วิกฤต', high: 'สูง', medium: 'ปานกลาง', low: 'ต่ำ',
};
const BAND_TONE: Record<RiskBand, BadgeTone> = {
  critical: 'danger', high: 'warning', medium: 'accent', low: 'neutral',
};
const STATUS_LABEL: Record<OpsRiskStatus, string> = {
  open: 'ยังไม่จัดการ', mitigating: 'กำลังลดความเสี่ยง',
  accepted_signed_off: 'ยอมรับและลงนามแล้ว', resolved: 'จัดการเรียบร้อย',
};

/*
 * Likelihood and impact are forced to 1–5 by the server (`admin.ts:383`) and the score is their
 * product, so the form offers the five steps rather than a free number that can be refused.
 */
const SCALE = [1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: String(n) }));

const BLANK_RISK = { title: '', category: '', likelihood: 3, impact: 3, mitigationPlan: '' };

export function OpsRisksScreen({ destination, state, permitted, role }: {
  destination: Destination;
  state: OpsRisksState;
  permitted: boolean;
  role?: string | null;
}) {
  const {
    risks, staff, loading, loadError, error, busyId, canSignOff,
    create, update, setStatus, signOff, remove, reload,
  } = state;
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(BLANK_RISK);
  const [openId, setOpenId] = useState<string | null>(null);
  const [waiver, setWaiver] = useState('');
  const [conditions, setConditions] = useState('');

  const open = openId ? risks.find((r) => r.id === openId) ?? null : null;
  const live = risks.filter((r) => r.status !== 'resolved');
  const critical = live.filter((r) => bandOf(r.score) === 'critical');
  const unowned = live.filter((r) => !r.ownerStaffId && !r.importedOwnerName.trim());
  const unmitigated = live.filter((r) => !r.mitigationPlan.trim());

  /** The 5×5 grid the frame draws: likelihood down, impact across. */
  const cell = (likelihood: number, impact: number) =>
    live.filter((r) => r.likelihood === likelihood && r.impact === impact);

  const columns: Column<OpsRiskDto>[] = [
    {
      key: 'title',
      header: 'ความเสี่ยง',
      cell: (r) => (
        <span className="jt-cell-stack">
          <span>{r.title}</span>
          <span className="jt-cell-sub">{r.category || 'ไม่ระบุหมวด'}</span>
        </span>
      ),
    },
    {
      key: 'band',
      header: 'ระดับ',
      width: '190px',
      cell: (r) => (
        <span className="jt-cell-stack">
          <StatusBadge tone={BAND_TONE[bandOf(r.score)]}>{BAND_LABEL[bandOf(r.score)]}</StatusBadge>
          <span className="jt-cell-sub jt-mono">โอกาส {r.likelihood} × ผลกระทบ {r.impact} = {r.score}</span>
        </span>
      ),
    },
    {
      key: 'owner',
      header: 'ผู้รับผิดชอบ',
      width: '180px',
      cell: (r) => (r.ownerName || r.importedOwnerName || <StatusBadge tone="warning">ยังไม่มีเจ้าของ</StatusBadge>),
    },
    {
      key: 'mitigation',
      header: 'แผนรับมือ',
      cell: (r) => (r.mitigationPlan.trim()
        ? <span className="jt-cell-sub">{r.mitigationPlan}</span>
        : <StatusBadge tone="warning">ยังไม่มีแผน</StatusBadge>),
    },
    {
      key: 'status',
      header: 'สถานะ',
      width: '190px',
      cell: (r) => (
        <span className="jt-cell-stack">
          <StatusBadge tone={r.status === 'resolved' ? 'success' : 'neutral'}>{STATUS_LABEL[r.status]}</StatusBadge>
          {r.signoffs.length > 0 && <span className="jt-cell-sub">ลงนามโดย {r.signoffs[r.signoffs.length - 1].actor}</span>}
        </span>
      ),
    },
  ];

  return (
    <TableTemplate
      gate={{ destination, loading, error: loadError || null, onRetry: reload, permitted, role }}
      kpis={[
        <KpiCard key="crit" label="ระดับวิกฤต" value={critical.length.toLocaleString('th-TH')} tone={critical.length > 0 ? 'danger' : undefined} />,
        <KpiCard key="live" label="ที่ยังเปิดอยู่" value={live.length.toLocaleString('th-TH')} />,
        <KpiCard key="plan" label="ยังไม่มีแผนรับมือ" value={unmitigated.length.toLocaleString('th-TH')} tone={unmitigated.length > 0 ? 'warning' : undefined} />,
        <KpiCard key="owner" label="ยังไม่มีเจ้าของ" value={unowned.length.toLocaleString('th-TH')} tone={unowned.length > 0 ? 'warning' : undefined} />,
      ]}
      summary={
        <>
          {error && <Alert tone="danger" title="ทำรายการไม่สำเร็จ">{error}</Alert>}
          {critical.length > 0 && (
            <Alert tone="danger" title={`${critical.length} ความเสี่ยงระดับวิกฤตยังเปิดอยู่`}>
              {critical.slice(0, 3).map((r) => r.title).join(' · ')}
              {critical.length > 3 && ' และอื่น ๆ'}
            </Alert>
          )}
          <Card title="ตารางความเสี่ยง" sub="โอกาสในแนวตั้ง ผลกระทบในแนวนอน — ตัวเลขคือจำนวนความเสี่ยงในช่องนั้น">
            <div className="jt-matrix" role="table" aria-label="ตารางโอกาสและผลกระทบ">
              {[5, 4, 3, 2, 1].map((likelihood) => (
                <div key={likelihood} className="jt-matrix-row" role="row">
                  <span className="jt-matrix-axis" role="rowheader">โอกาส {likelihood}</span>
                  {[1, 2, 3, 4, 5].map((impact) => {
                    const here = cell(likelihood, impact);
                    return (
                      <span
                        key={impact}
                        role="cell"
                        className="jt-matrix-cell"
                        data-band={bandOf(likelihood * impact)}
                        title={here.map((r) => r.title).join(', ') || 'ไม่มีความเสี่ยงในช่องนี้'}
                      >
                        <span className="jt-sr-only">
                          โอกาส {likelihood} ผลกระทบ {impact} ระดับ{BAND_LABEL[bandOf(likelihood * impact)]} —
                        </span>
                        {here.length || ''}
                      </span>
                    );
                  })}
                </div>
              ))}
              <div className="jt-matrix-row" role="row">
                <span className="jt-matrix-axis" aria-hidden="true" />
                {[1, 2, 3, 4, 5].map((impact) => (
                  <span key={impact} className="jt-matrix-axis" role="columnheader">ผลกระทบ {impact}</span>
                ))}
              </div>
            </div>
          </Card>
        </>
      }
      toolbar={permitted && (
        <Button size="sm" variant="primary" onClick={() => { setDraft(BLANK_RISK); setAdding(true); }}>
          ＋ เพิ่มความเสี่ยง
        </Button>
      )}
      table={{
        label: 'ทะเบียนความเสี่ยง',
        columns,
        rows: risks,
        rowKey: (r) => r.id,
        emptyTitle: 'ยังไม่มีความเสี่ยงในทะเบียน',
        onRowClick: (r) => { setOpenId(r.id); setWaiver(''); setConditions(''); },
        activeRowKey: openId,
        card: (r) => (
          <span className="jt-cell-stack">
            <span>{r.title}</span>
            <StatusBadge tone={BAND_TONE[bandOf(r.score)]}>{BAND_LABEL[bandOf(r.score)]}</StatusBadge>
          </span>
        ),
      }}
      drawer={
        <>
        {/*
          * The register had no way to put a risk into it — `useOpsRisks` has carried `create`
          * since the rebuild and no control ever called it, so the matrix above could only ever
          * be empty.
          */}
        <FormDialog
          open={adding}
          onClose={() => setAdding(false)}
          onSubmit={() => {
            void create({
              title: draft.title.trim(),
              category: draft.category.trim(),
              likelihood: draft.likelihood,
              impact: draft.impact,
              mitigationPlan: draft.mitigationPlan.trim() || undefined,
            }).then(() => setAdding(false));
          }}
          title="เพิ่มความเสี่ยง"
          description="คะแนน = โอกาสเกิด × ผลกระทบ · ตั้งแต่ 12 ขึ้นไปถือว่าต้องมีคนรับผิดชอบคำตอบ"
          submitLabel="เพิ่มความเสี่ยง"
          error={error || null}
        >
          <TextField
            label="ความเสี่ยงคืออะไร"
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            hint="เขียนให้คนอ่านรู้ว่าอะไรจะพัง เช่น ฝนตกหนักช่วงเปิดประตู"
          />
          <TextField
            label="หมวด"
            value={draft.category}
            onChange={(e) => setDraft({ ...draft, category: e.target.value })}
            hint="เช่น สภาพอากาศ · ความปลอดภัย · ผู้ขาย"
          />
          <Select
            label="โอกาสเกิด (1–5)"
            value={String(draft.likelihood)}
            options={SCALE}
            onChange={(e) => setDraft({ ...draft, likelihood: Number(e.target.value) })}
          />
          <Select
            label="ผลกระทบ (1–5)"
            value={String(draft.impact)}
            options={SCALE}
            onChange={(e) => setDraft({ ...draft, impact: Number(e.target.value) })}
          />
          <Textarea
            label="แผนลดความเสี่ยง"
            value={draft.mitigationPlan}
            onChange={(e) => setDraft({ ...draft, mitigationPlan: e.target.value })}
            hint="เว้นว่างได้ เติมทีหลังในรายละเอียดของความเสี่ยงนั้น"
          />
        </FormDialog>

        <Drawer
          open={!!open}
          onClose={() => setOpenId(null)}
          title={open?.title ?? ''}
          footer={open && (
            <Button variant="ghost" disabled={busyId === open.id} onClick={() => void remove(open)}>ลบความเสี่ยง</Button>
          )}
        >
          {open && (
            <>
              <div className="jt-inline-actions">
                <StatusBadge tone={BAND_TONE[bandOf(open.score)]}>{BAND_LABEL[bandOf(open.score)]}</StatusBadge>
                <span className="jt-field-hint jt-mono">
                  โอกาส {open.likelihood} × ผลกระทบ {open.impact} = {open.score}
                </span>
              </div>

              {!open.mitigationPlan.trim() && open.status !== 'resolved' && (
                <Alert tone="warning" title="ยังไม่มีแผนรับมือ">
                  ความเสี่ยงที่ไม่มีแผนคือความเสี่ยงที่ยังไม่มีใครตัดสินใจอะไรกับมัน
                </Alert>
              )}

              <Card title="การประเมิน">
                <div className="jt-field-grid">
                  <Select
                    label="โอกาสเกิด"
                    value={String(open.likelihood)}
                    disabled={busyId === open.id}
                    options={[1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: String(n) }))}
                    onChange={(e) => void update(open, { likelihood: Number(e.target.value) })}
                  />
                  <Select
                    label="ผลกระทบ"
                    value={String(open.impact)}
                    disabled={busyId === open.id}
                    options={[1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: String(n) }))}
                    onChange={(e) => void update(open, { impact: Number(e.target.value) })}
                  />
                </div>
                <Select
                  label="ผู้รับผิดชอบ"
                  value={open.ownerStaffId ?? ''}
                  disabled={busyId === open.id}
                  options={[{ value: '', label: 'ยังไม่มีเจ้าของ' }, ...staff.map((s) => ({ value: s.id, label: s.displayName }))]}
                  onChange={(e) => void update(open, { ownerStaffId: e.target.value || null })}
                />
                <Select
                  label="สถานะ"
                  value={open.status}
                  disabled={busyId === open.id || open.status === 'accepted_signed_off'}
                  options={(['open', 'mitigating', 'resolved'] as OpsRiskStatus[]).map((s) => ({ value: s, label: STATUS_LABEL[s] }))}
                  onChange={(e) => void setStatus(open, e.target.value as OpsRiskStatus)}
                  hint={open.status === 'accepted_signed_off' ? 'ความเสี่ยงที่ลงนามยอมรับแล้วเปลี่ยนสถานะจากที่นี่ไม่ได้' : undefined}
                />
                <Textarea
                  label="แผนรับมือ"
                  value={open.mitigationPlan}
                  onChange={(e) => void update(open, { mitigationPlan: e.target.value })}
                />
              </Card>

              <Card
                title="ยอมรับความเสี่ยงนี้"
                sub="ใช้เมื่อเลือกที่จะรับผลแทนการลดความเสี่ยง — ไม่ใช่การปิดเรื่อง"
              >
                {open.signoffs.length > 0 ? (
                  <ul className="jt-order-lines">
                    {open.signoffs.map((s) => (
                      <li key={s.id}>
                        <span>{s.actor}</span>
                        <span className="jt-cell-sub">{new Date(s.signedAt).toLocaleString('th-TH')}</span>
                      </li>
                    ))}
                  </ul>
                ) : !canSignOff ? (
                  <p className="jt-field-hint">การลงนามยอมรับความเสี่ยงเป็นสิทธิ์ของเจ้าของอีเวนต์</p>
                ) : (
                  <>
                    <Alert tone="warning" title="ลงนามแล้วความเสี่ยงยังอยู่">
                      การยอมรับหมายถึงตัดสินใจว่าจะรับผลถ้ามันเกิด ไม่ได้ทำให้ความเสี่ยงหายไป และบันทึกไว้พร้อมชื่อผู้ลงนาม
                    </Alert>
                    <Textarea
                      label="เหตุผลที่ยอมรับ"
                      requiredMark
                      value={waiver}
                      onChange={(e) => setWaiver(e.target.value)}
                    />
                    <TextField
                      label="เงื่อนไขที่ต้องทำควบคู่"
                      value={conditions}
                      onChange={(e) => setConditions(e.target.value)}
                      hint="เช่น ต้องมีทีมสำรองประจำจุดตลอดงาน"
                    />
                    <div className="jt-inline-actions">
                      <Button
                        variant="destructive"
                        disabled={busyId === open.id || !waiver.trim()}
                        onClick={async () => {
                          await signOff(open, waiver.trim(), conditions.trim());
                          setWaiver('');
                          setConditions('');
                        }}
                      >
                        ลงนามยอมรับความเสี่ยง
                      </Button>
                    </div>
                  </>
                )}
              </Card>
            </>
          )}
        </Drawer>
        </>
      }
    />
  );
}

export function OpsRisksDestination({ token, eventSlug, destination, permitted, role }: {
  token: string;
  eventSlug?: string;
  destination: Destination;
  permitted: boolean;
  role?: string | null;
}) {
  const state = useOpsRisks(token, eventSlug, role ?? null);
  return <OpsRisksScreen destination={destination} state={state} permitted={permitted} role={role} />;
}
